/**
 * 訪談圖片牆 Modal(REQ-20260421007)。
 *
 * - 一次抓該客戶所有圖片附件(GET /customers/:cid/attachments?kind=image),
 *   後端依 interview.createdAt desc, attachment.createdAt desc 排好。
 * - 每張縮圖用 IntersectionObserver 懶載入 signed URL,避免一次打爆 API。
 * - 點縮圖 → 重用 ImageLightbox,prev/next 範圍是該客戶「所有」圖片。
 * - 已存的檢視角度(REQ-20260909004)縮圖與放大檢視都會套用;有寫入權限的角色
 *   才拿得到放大檢視裡的存檔鈕。**存的只是角度,原圖與 EXIF 不動**。
 */
import { useEffect, useRef, useState } from 'react';
import type { AttachmentRotation, CustomerImageAttachment } from '@app/shared';
import { Modal } from '../../components/Modal';
import {
  fetchDownloadUrl,
  saveAttachmentRotation,
  useCustomerImageAttachments,
} from '../attachments/api';
import { ImageLightbox, type LightboxImage } from '../attachments/ImageLightbox';
import { useAuthStore } from '../auth/store';

interface Props {
  open: boolean;
  onClose: () => void;
  customerId: number;
  /** 點縮圖「訪談」按鈕、或放大檢視的「看訪談」→ 父層關閉 wall + 跳到該訪談並高亮。 */
  onJumpToInterview?: (interviewId: number) => void;
}

export function InterviewImageWall({
  open,
  onClose,
  customerId,
  onJumpToInterview,
}: Props): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useCustomerImageAttachments(
    customerId,
    open,
  );
  const images = data?.data ?? [];
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // 回存角度的權限:寫法比照 InterviewTimeline 的 canWrite,VIEWER 拿不到存檔鈕。
  // 圖片牆是跨訪談的清單,手上沒有每張圖所屬客戶的 ownerId,所以 SALES 一律先放行 ——
  // 真正的擁有權由後端的 AttachmentAccessGuard(uuid → customer.ownerId)把關。
  const me = useAuthStore((s) => s.user);
  const canWrite = me?.role === 'ADMIN' || me?.role === 'SALES';

  const lightboxImages: LightboxImage[] = images.map((a) => ({
    uuid: a.uuid,
    originalName: a.originalName,
    interviewId: a.interviewId,
    rotation: a.rotation,
  }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`圖檔瀏覽${data ? `（${images.length} 張）` : ''}`}
      widthClass="max-w-6xl"
    >
      {isLoading ? (
        <div className="text-slate-500 py-8 text-center">載入中…</div>
      ) : isError ? (
        <div role="alert" className="text-red-600 py-8 text-center">
          載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
        </div>
      ) : images.length === 0 ? (
        <div className="text-slate-500 py-8 text-center">目前沒有圖片附件</div>
      ) : (
        <ul
          className="grid gap-2"
          style={{
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          }}
          data-testid="image-wall-grid"
        >
          {images.map((img, i) => (
            <Thumbnail
              key={img.uuid}
              image={img}
              onOpen={() => setLightboxIndex(i)}
              {...(onJumpToInterview
                ? { onJump: () => onJumpToInterview(img.interviewId) }
                : {})}
            />
          ))}
        </ul>
      )}

      {lightboxIndex !== null ? (
        <ImageLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          {...(onJumpToInterview
            ? {
                onJumpToInterview: (interviewId: number) => {
                  setLightboxIndex(null);
                  onJumpToInterview(interviewId);
                },
              }
            : {})}
          {...(canWrite
            ? {
                // 存完重取整份清單,新的 rotation 才會同時回流到縮圖與
                // lightbox 的「已存角度」
                onSaveRotation: async (
                  image: LightboxImage,
                  rotation: number,
                ): Promise<void> => {
                  await saveAttachmentRotation(
                    image.uuid,
                    rotation as AttachmentRotation,
                  );
                  await refetch();
                },
              }
            : {})}
        />
      ) : null}
    </Modal>
  );
}

function Thumbnail({
  image,
  onOpen,
  onJump,
}: {
  image: CustomerImageAttachment;
  onOpen: () => void;
  onJump?: () => void;
}): JSX.Element {
  const ref = useRef<HTMLLIElement | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || url) return;
    let cancelled = false;
    fetchDownloadUrl(image.uuid)
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '載入失敗');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [inView, url, image.uuid]);

  const dateStr = new Date(image.interviewCreatedAt).toLocaleDateString('zh-TW');

  return (
    <li
      ref={ref}
      className="group relative border border-slate-200 rounded overflow-hidden bg-slate-50 hover:border-emerald-400 hover:shadow-md transition-colors"
      data-testid="image-wall-thumb"
    >
      <div className="relative">
        <button
          type="button"
          onClick={onOpen}
          className="block w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          title={`${image.originalName}（${dateStr}）`}
        >
          <div className="aspect-square bg-slate-100 flex items-center justify-center text-xs text-slate-400">
            {error ? (
              <span className="text-red-500 px-2 text-center">載入失敗</span>
            ) : url ? (
              // 縮圖也照已存的角度顯示(REQ-20260909004),不然清單裡還是躺的。
              // 這個格子是正方形(aspect-square)且 img 剛好填滿它,轉 90/270 度
              // 之後外框仍是同一個正方形 —— 不需要 ImageLightbox 那段
              // maxWidth/maxHeight 對調(那是給 max-w-[90vw]/max-h-[85vh] 這種
              // 長寬不等的框用的),外層也還有 overflow-hidden 兜底。
              <img
                src={url}
                alt={image.originalName}
                loading="lazy"
                className="w-full h-full object-cover"
                style={{ transform: `rotate(${image.rotation}deg)` }}
              />
            ) : (
              <span>載入中…</span>
            )}
          </div>
        </button>
        {/* hover overlay:半透明遮罩 + 兩個動作按鈕 */}
        <div
          className="absolute inset-0 bg-black/45 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none"
        >
          <button
            type="button"
            onClick={onOpen}
            className="bg-white/95 hover:bg-white text-slate-800 text-xs font-medium rounded px-2.5 py-1 shadow pointer-events-auto"
            data-testid="image-wall-view-btn"
          >
            檢視
          </button>
          {onJump ? (
            <button
              type="button"
              onClick={onJump}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded px-2.5 py-1 shadow pointer-events-auto"
              data-testid="image-wall-goto-btn"
            >
              訪談
            </button>
          ) : null}
        </div>
      </div>
      <div className="px-1.5 py-1 text-[10px] text-slate-500 text-center">{dateStr}</div>
    </li>
  );
}
